import React, { useState } from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';

const Fraud = () => {
  // Hypothetical JSON data with detailed incident information
  const data = [
    {
      category: "Travel Expenses",
      incidents: 8,
      riskScore: 75,
      incidentDetails: [
        { id: 1, description: "Unauthorized flight booking", date: "2025-01-15", period: "January 2025", occurrenceCount: 1 },
        { id: 2, description: "Hotel expense overrun", date: "2025-01-16", period: "January 2025", occurrenceCount: 2 },
        { id: 3, description: "Car rental discrepancy", date: "2025-01-17", period: "January 2025", occurrenceCount: 1 }
      ]
    },
    {
      category: "Procurement",
      incidents: 5,
      riskScore: 60,
      incidentDetails: [
        { id: 4, description: "Conflict of interest in vendor selection", date: "2025-02-10", period: "February 2025", occurrenceCount: 1 },
        { id: 5, description: "Suspicious procurement pattern", date: "2025-02-11", period: "February 2025", occurrenceCount: 1 }
      ]
    },
    {
      category: "Payroll",
      incidents: 3,
      riskScore: 50,
      incidentDetails: [
        { id: 6, description: "Incorrect overtime calculations", date: "2025-03-05", period: "March 2025", occurrenceCount: 1 }
      ]
    },
    {
      category: "Vendor Payments",
      incidents: 12,
      riskScore: 90,
      incidentDetails: [
        { id: 7, description: "Duplicate invoice submission", date: "2025-04-01", period: "April 2025", occurrenceCount: 2 },
        { id: 8, description: "Vendor kickback scheme", date: "2025-04-02", period: "April 2025", occurrenceCount: 1 }
      ]
    },
    {
      category: "Asset Misuse",
      incidents: 4,
      riskScore: 55,
      incidentDetails: [
        { id: 9, description: "Unauthorized equipment use", date: "2025-05-12", period: "May 2025", occurrenceCount: 1 }
      ]
    },
    {
      category: "Financial Reporting",
      incidents: 2,
      riskScore: 40,
      incidentDetails: [
        { id: 10, description: "Misstated financial statement", date: "2025-06-20", period: "June 2025", occurrenceCount: 1 }
      ]
    }
  ];

  // Create a list of categories dynamically, including an "All" option.
  const categories = ["All", ...Array.from(new Set(data.map(item => item.category)))];

  // State for the selected category
  const [selectedCategory, setSelectedCategory] = useState("All");

  // Filter the data: if "All" is selected, show all data; otherwise, filter by the selected category.
  const filteredData = selectedCategory === "All" ? data : data.filter(item => item.category === selectedCategory);

  return (
    <div className="p-4 md:p-8 bg-gray-50 rounded-lg shadow-md">
      <h2 className="text-xl font-semibold text-gray-700 mb-4">Fraud Risk Indicators</h2>

      {/* Dropdown Filter */}
      <div className="mb-4">
        <select
          value={selectedCategory}
          onChange={e => setSelectedCategory(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-md w-full"
        >
          {categories.map((cat, index) => (
            <option key={index} value={cat}>
              {cat}
            </option>
          ))}
        </select>
      </div>

      {/* Chart Section */}
      <ResponsiveContainer width="100%" height={400}>
        <BarChart data={filteredData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="category" />
          <YAxis />
          <Tooltip />
          <Legend />
          <Bar dataKey="incidents" name="Incidents" fill="#8884d8" />
          <Bar dataKey="riskScore" name="Risk Score" fill="#82ca9d" />
        </BarChart>
      </ResponsiveContainer>

      {/* Table Section for Examined Data */}
      <div className="mt-8 overflow-x-auto">
        <h3 className="text-lg font-semibold text-gray-700 mb-2">Examined Data</h3>
        <table className="min-w-full border-collapse">
          <thead>
            <tr>
              <th className="px-4 py-2 border">Category</th>
              <th className="px-4 py-2 border">Incidents</th>
              <th className="px-4 py-2 border">Risk Score</th>
            </tr>
          </thead>
          <tbody>
            {filteredData.map(item => (
              <tr key={item.category}>
                <td className="px-4 py-2 border">{item.category}</td>
                <td className="px-4 py-2 border">{item.incidents}</td>
                <td className="px-4 py-2 border">{item.riskScore}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Incidents Details Section */}
      <div className="mt-8">
        <h3 className="text-lg font-semibold text-gray-700 mb-2">Incidents Details</h3>
        <ul className="list-disc pl-5">
          {filteredData.map(item => (
            <li key={item.category}>
              <strong>{item.category}</strong> ({item.incidents} incidents)
              {item.incidentDetails && item.incidentDetails.length > 0 && (
                <ul className="list-disc pl-5 mt-1">
                  {item.incidentDetails.map(detail => (
                    <li key={detail.id}>
                      <div>
                        <span>{detail.description}</span>
                        <br />
                        <span>
                          <em>Date:</em> {detail.date} | <em>Period:</em> {detail.period} | <em>Occurrence:</em> {detail.occurrenceCount}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      </div>

      {/* Risk Score Calculation Explanation */}
      <div className="mt-8 p-4 bg-white border border-gray-200 rounded-md">
        <h3 className="text-lg font-semibold text-gray-700 mb-2">Risk Score Calculation</h3>
        <p className="text-gray-700 text-sm">
          The risk score for each category is calculated by considering several factors:
        </p>
        <ul className="list-disc pl-5 text-gray-700 text-sm mt-2">
          <li>The total number of incidents in the category.</li>
          <li>The frequency of occurrence, where repeated incidents have a higher impact.</li>
          <li>The period during which the incidents occurred, which can indicate recency or clustering of events.</li>
          <li>A severity factor applied based on historical data and potential financial impact.</li>
        </ul>
        <p className="text-gray-700 text-sm mt-2">
          In this hypothetical example, higher incident counts and repeated occurrences (as shown by the occurrence count) contribute to a higher risk score.
        </p>
      </div>
    </div>
  );
};

export default Fraud;
